/**
 * Modal — créer une nouvelle conversation (directe ou groupe).
 */
import { useState } from 'react';
import { X, Search, Users, User, Check } from 'lucide-react';
import {
  useListerContactsQuery, useCreerConversationMutation,
  type ConversationItem, type TypeConv,
} from '../../services/messagerieChat';

export function NouvelleConversationModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (c: ConversationItem) => void;
}) {
  const [type, setType] = useState<TypeConv>('direct');
  const [libelle, setLibelle] = useState('');
  const [description, setDescription] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Record<number, string>>({});
  const { data } = useListerContactsQuery({ q: q || undefined });
  const [creer, { isLoading }] = useCreerConversationMutation();

  const toggle = (uid: number, nom: string) => {
    setSelected(prev => {
      const c = { ...prev };
      if (c[uid]) delete c[uid];
      else c[uid] = nom;
      // Direct : max 1
      if (type === 'direct' && Object.keys(c).length > 1) {
        return { [uid]: nom };
      }
      return c;
    });
  };

  const handleCreer = async () => {
    const ids = Object.keys(selected).map(Number);
    if (ids.length === 0) return;
    if (type !== 'direct' && !libelle.trim()) {
      alert('Nom du groupe requis');
      return;
    }
    try {
      const c = await creer({
        type_conv: type,
        libelle: libelle.trim(),
        description: description.trim(),
        participants_user_ids: ids,
      }).unwrap();
      onCreated(c);
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur création');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-950 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-3 border-b border-slate-200 dark:border-gray-800 flex items-center justify-between">
          <h2 className="font-bold text-slate-900 dark:text-white">Nouvelle conversation</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-gray-800 rounded">
            <X size={16} />
          </button>
        </div>

        {/* Type */}
        <div className="p-3 border-b border-slate-200 dark:border-gray-800 flex gap-2">
          <button onClick={() => setType('direct')}
            className={`flex-1 py-2 rounded flex items-center justify-center gap-1.5 text-xs ${
              type === 'direct' ? 'bg-amber-600 text-white' : 'bg-slate-100 dark:bg-gray-900 text-slate-600 dark:text-gray-400'
            }`}>
            <User size={13} /> Direct
          </button>
          <button onClick={() => setType('groupe')}
            className={`flex-1 py-2 rounded flex items-center justify-center gap-1.5 text-xs ${
              type === 'groupe' ? 'bg-amber-600 text-white' : 'bg-slate-100 dark:bg-gray-900 text-slate-600 dark:text-gray-400'
            }`}>
            <Users size={13} /> Groupe
          </button>
        </div>

        {/* Nom du groupe */}
        {type !== 'direct' && (
          <div className="p-3 space-y-2 border-b border-slate-200 dark:border-gray-800">
            <input value={libelle} onChange={(e) => setLibelle(e.target.value)}
              placeholder="Nom du groupe" maxLength={200}
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-slate-900 dark:text-white" />
            <input value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Description (optionnel)" maxLength={500}
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-slate-900 dark:text-white" />
          </div>
        )}

        {/* Recherche contacts */}
        <div className="p-3 border-b border-slate-200 dark:border-gray-800">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-2.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher..." autoFocus
              className="w-full pl-7 pr-2 py-1.5 text-xs bg-slate-100 dark:bg-gray-900 border-0 rounded" />
          </div>
        </div>

        {/* Sélection contacts */}
        <div className="flex-1 overflow-y-auto p-1">
          {(data?.results || []).map(c => (
            <button key={c.user_id} onClick={() => toggle(c.user_id, c.nom)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-slate-100 dark:hover:bg-gray-800 ${
                selected[c.user_id] ? 'bg-amber-50 dark:bg-amber-900/20' : ''
              }`}>
              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                selected[c.user_id] ? 'bg-amber-600 border-amber-600' : 'border-slate-300'
              }`}>
                {selected[c.user_id] && <Check size={10} className="text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{c.nom}</div>
                <div className="text-[10px] text-slate-500">{c.role_label}</div>
              </div>
              {c.en_ligne && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
            </button>
          ))}
        </div>

        <div className="p-3 border-t border-slate-200 dark:border-gray-800 flex items-center gap-2">
          <div className="flex-1 text-xs text-slate-500">
            {Object.keys(selected).length} sélectionné(s)
          </div>
          <button onClick={handleCreer}
            disabled={isLoading || Object.keys(selected).length === 0}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded">
            Créer
          </button>
        </div>
      </div>
    </div>
  );
}
